import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType, LoginResponse } from '@/types/auth';
import { APP_VERSION } from '@/version';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    const storedVersion = localStorage.getItem('app_version');
    
    // Check if app version has changed
    if (storedVersion && storedVersion !== APP_VERSION) {
      console.log('Detekována nová verze aplikace, probíhá odhlášení uživatele...');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('app_version');
      setIsLoading(false);
      return;
    }
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData) as User;
        setUser(parsedUser);
        // Store current version
        localStorage.setItem('app_version', APP_VERSION);
      } catch (error) {
        console.error('Chyba při načítání uživatelských dat:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('app_version');
      }
    }
    
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Chyba při přihlašování');
      }

      const loginData: LoginResponse = await response.json();
      
      if (!loginData.success) {
        throw new Error(loginData.message || 'Nesprávné přihlašovací údaje');
      }

      // Store session data
      localStorage.setItem('auth_token', loginData.token);
      localStorage.setItem('user_data', JSON.stringify(loginData.user));
      localStorage.setItem('app_version', APP_VERSION);
      
      setUser(loginData.user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('app_version');
    setUser(null);
  };

  const changePassword = async (newPassword: string): Promise<void> => {
    if (!user) {
      throw new Error('Uživatel není přihlášen');
    }

    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ newPassword }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Chyba při změně hesla');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Chyba při změně hesla');
      }

      // Update user data to reflect custom password
      const updatedUser = { ...user, has_custom_password: true };
      localStorage.setItem('user_data', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth musí být použit uvnitř AuthProvider');
  }
  return context;
} 